import { io as socketIo } from "socket.io-client";
import {
  CanvasRepairAckSchema,
  CanvasRevokedSchema,
  CanvasVerifyAckSchema,
  ErrorCodeSchema,
  PresenceSnapshotSchema,
  SOCKET_NAMESPACE,
  SOCKET_TRANSPORT_PATH,
  ServerPointerUpdateSchema,
  createJoinAckSchema,
  createMutationAckSchema,
  createScopedCanonicalUpdateSchema,
  createScopedLessonStateSchema,
} from "@canvaschool/contracts";

import { ApiError } from "../api/client";

import type {
  CanonicalUpdate,
  CanvasDiagnostic,
  CanvasMutation,
  CanvasRevoked,
  CanvasRepairAck,
  CanvasRepairRequest,
  CanvasVerifyAck,
  CompletionModeRequest,
  CompletionReset,
  CompletionToggle,
  ErrorCode,
  JoinResult,
  LessonState,
  LockRequest,
  MutationAck,
  PointerUpdate,
  PresenceSnapshot,
  PresenceUpdate,
  ServerPointerUpdate,
  StudentCommentsRequest,
} from "@canvaschool/contracts";

/** The part of a Socket.IO client socket this transport uses. */
export type SocketLike = {
  connected: boolean;
  /** False once the server refused the connection in its middleware. */
  active: boolean;
  connect(): unknown;
  disconnect(): unknown;
  emit(event: string, payload: unknown): unknown;
  on(event: string, listener: (...args: any[]) => void): unknown;
  removeAllListeners(): unknown;
  timeout(milliseconds: number): {
    emit(
      event: string,
      payload: unknown,
      callback: (error: Error | null, ack?: unknown) => void,
    ): unknown;
  };
  volatile: { emit(event: string, payload: unknown): unknown };
};

export type TransportEvents = {
  onJoined(result: JoinResult): void;
  onJoinFailed(code: ErrorCode): void;
  onCanonical(update: CanonicalUpdate): void;
  onLesson(state: LessonState): void;
  onPresence(snapshot: PresenceSnapshot): void;
  onPointer(update: ServerPointerUpdate): void;
  onRevoked(event: CanvasRevoked): void;
  onDisconnected(): void;
  /** The connection was refused for good (or the session is gone). */
  onAccessLost(code: ErrorCode): void;
};

export type CollaborationTransportOptions = {
  classId: string;
  canvasId: string;
  /** The current session's CSRF token, or null once signed out. */
  getCsrfToken(): Promise<string | null>;
  events: TransportEvents;
  io?: (uri: string, options: Record<string, unknown>) => SocketLike;
  ackTimeoutMilliseconds?: number;
  retryDelayMilliseconds?: (attempt: number) => number;
  setTimer?: (callback: () => void, milliseconds: number) => () => void;
};

export type CollaborationTransport = {
  connect(): void;
  /** Drops the connection and connects again with a fresh token. */
  reconnect(): void;
  close(): void;
  sendMutation(mutation: CanvasMutation): Promise<MutationAck>;
  sendPointer(update: PointerUpdate): void;
  sendLock(request: LockRequest): void;
  /** False when nothing was sent (no live connection). */
  sendCompletion(toggle: CompletionToggle): boolean;
  sendCompletionMode(request: CompletionModeRequest): void;
  sendCompletionReset(request: CompletionReset): void;
  sendStudentComments(request: StudentCommentsRequest): void;
  sendPresence(update: PresenceUpdate): void;
  /**
   * Asks which scene hash the server holds for the version the editor
   * believes it is at (Figma-style divergence detection). Rejects quietly
   * on any transport problem — no live connection, a timeout, or an old
   * server that doesn't know the event at all — so a caller that doesn't
   * await it can't turn a missing feature into an unhandled rejection.
   */
  verify(canvasId: string): Promise<CanvasVerifyAck>;
  /** Asks for only the elements that differ from the manifest
   * (`canvas:repair`); rejects quietly on any transport problem or an old
   * server, exactly like `verify`. */
  repair(request: CanvasRepairRequest): Promise<CanvasRepairAck>;
  /** Fire-and-forget operator diagnostic; silently dropped without a live
   * connection, same as an old server that doesn't know the event. */
  diagnostic(payload: CanvasDiagnostic): void;
};

export type TransportFactory = (
  options: CollaborationTransportOptions,
) => CollaborationTransport;

const AUTHENTICATION = new Set<ErrorCode>(["UNAUTHENTICATED", "CSRF_INVALID"]);
const TRANSIENT = new Set<ErrorCode>(["RATE_LIMITED", "PERSISTENCE_FAILED"]);
/** Token fetch failures worth retrying; any other refusal is final. */
const TRANSIENT_FETCH = new Set<string>([
  ...TRANSIENT,
  "NETWORK_FAILED",
  "INVALID_RESPONSE",
]);
/** A rotated cookie needs one fresh token; repeated refusals mean signed out. */
const MAX_AUTHENTICATION_RETRIES = 3;

const defaultTimer = (callback: () => void, milliseconds: number) => {
  const handle = window.setTimeout(callback, milliseconds);
  return () => window.clearTimeout(handle);
};

const defaultRetryDelay = (attempt: number) =>
  Math.min(30_000, 1_000 * 2 ** attempt);

const defaultIo = socketIo as unknown as NonNullable<
  CollaborationTransportOptions["io"]
>;

/** Same-origin Socket.IO connection to one canvas with typed acknowledgements. */
export const createCollaborationTransport: TransportFactory = (options) => {
  const { classId, canvasId, events } = options;
  const setTimer = options.setTimer ?? defaultTimer;
  const retryDelay = options.retryDelayMilliseconds ?? defaultRetryDelay;
  const ackTimeout = options.ackTimeoutMilliseconds ?? 15_000;
  const joinAckSchema = createJoinAckSchema(classId, canvasId);
  const canonicalSchema = createScopedCanonicalUpdateSchema(canvasId);
  const lessonSchema = createScopedLessonStateSchema(canvasId);

  let socket: SocketLike | null = null;
  let closed = false;
  let sessionGone = false;
  /** The token fetch failed (offline or server error); keep trying. */
  let tokenUnavailable = false;
  let revokedByServer = false;
  let connectAttempts = 0;
  let joinAttempts = 0;
  const timers = new Set<() => void>();

  const cancelTimers = () => {
    for (const cancel of timers) {
      cancel();
    }
    timers.clear();
  };

  const later = (callback: () => void, attempt: number) => {
    const cancel = setTimer(() => {
      timers.delete(cancel);
      if (!closed) {
        callback();
      }
    }, retryDelay(attempt));
    timers.add(cancel);
  };

  const join = () => {
    const current = socket;
    if (!current || closed || !current.connected) {
      return;
    }
    current
      .timeout(ackTimeout)
      .emit("canvas:join", { classId, canvasId }, (error, ack) => {
        if (closed || socket !== current) {
          return;
        }
        if (error && !current.connected) {
          // The next "connect" joins again; a timer would only duplicate it.
          return;
        }
        const parsed = error ? null : joinAckSchema.safeParse(ack);
        if (parsed?.success && !("ok" in parsed.data)) {
          joinAttempts = 0;
          events.onJoined(parsed.data);
          return;
        }
        const code =
          parsed?.success && "ok" in parsed.data ? parsed.data.code : null;
        if (code && AUTHENTICATION.has(code)) {
          reconnect();
        } else if (code === null || TRANSIENT.has(code)) {
          later(join, joinAttempts++);
        } else {
          events.onJoinFailed(code);
        }
      });
  };

  const attach = (current: SocketLike) => {
    current.on("connect", () => {
      cancelTimers();
      connectAttempts = 0;
      revokedByServer = false;
      join();
    });
    current.on(
      "connect_error",
      (error: Error & { data?: { code?: unknown } }) => {
        // An active client retries transport failures by itself.
        if (closed || current.active || sessionGone) {
          return;
        }
        if (tokenUnavailable) {
          later(() => {
            if (socket === current) {
              current.connect();
            }
          }, connectAttempts++);
          return;
        }
        const parsed = ErrorCodeSchema.safeParse(
          error.data?.code ?? error.message,
        );
        const code: ErrorCode = parsed.success
          ? parsed.data
          : "PERSISTENCE_FAILED";
        const retry =
          TRANSIENT.has(code) ||
          (AUTHENTICATION.has(code) &&
            connectAttempts < MAX_AUTHENTICATION_RETRIES);
        if (retry) {
          later(() => {
            if (socket === current) {
              current.connect();
            }
          }, connectAttempts++);
          return;
        }
        events.onAccessLost(code);
      },
    );
    current.on("disconnect", (reason: string) => {
      events.onDisconnected();
      // The client does not reconnect after a server-side disconnect; one
      // without a revocation (such as a restart) is worth another attempt.
      if (reason === "io server disconnect" && !revokedByServer && !closed) {
        later(() => {
          if (socket === current) {
            current.connect();
          }
        }, connectAttempts++);
      }
    });
    current.on("canvas:canonical", (payload: unknown) => {
      const parsed = canonicalSchema.safeParse(payload);
      if (parsed.success) {
        events.onCanonical(parsed.data);
      }
    });
    current.on("lesson:state", (payload: unknown) => {
      const parsed = lessonSchema.safeParse(payload);
      if (parsed.success) {
        events.onLesson(parsed.data);
      }
    });
    current.on("presence:snapshot", (payload: unknown) => {
      const parsed = PresenceSnapshotSchema.safeParse(payload);
      if (parsed.success && parsed.data.canvasId === canvasId) {
        events.onPresence(parsed.data);
      }
    });
    current.on("pointer:update", (payload: unknown) => {
      const parsed = ServerPointerUpdateSchema.safeParse(payload);
      if (parsed.success && parsed.data.canvasId === canvasId) {
        events.onPointer(parsed.data);
      }
    });
    current.on("canvas:revoked", (payload: unknown) => {
      const parsed = CanvasRevokedSchema.safeParse(payload);
      if (parsed.success && parsed.data.canvasId === canvasId) {
        revokedByServer = true;
        events.onRevoked(parsed.data);
      }
    });
  };

  const connect = () => {
    if (closed) {
      return;
    }
    if (!socket) {
      socket = (options.io ?? defaultIo)(SOCKET_NAMESPACE, {
        path: SOCKET_TRANSPORT_PATH,
        withCredentials: true,
        autoConnect: false,
        // Browsers omit Origin on same-origin polling requests, and the
        // server authenticates the handshake by Origin; WebSocket sends it.
        transports: ["websocket"],
        // Called on every (re)connection, so a rotated session is picked up.
        auth: (callback: (data: { csrfToken: string }) => void) => {
          options.getCsrfToken().then(
            (token) => {
              tokenUnavailable = false;
              sessionGone = token === null;
              if (token === null) {
                events.onAccessLost("UNAUTHENTICATED");
              }
              callback({ csrfToken: token ?? "" });
            },
            (error: unknown) => {
              const code =
                error instanceof ApiError ? error.code : "NETWORK_FAILED";
              if (
                TRANSIENT_FETCH.has(code) ||
                (error instanceof ApiError && error.status >= 500)
              ) {
                tokenUnavailable = true;
              } else {
                // For example a disabled account or an archived class.
                sessionGone = true;
                events.onAccessLost(code as ErrorCode);
              }
              callback({ csrfToken: "" });
            },
          );
        },
      });
      attach(socket);
    }
    socket.connect();
  };

  const reconnect = () => {
    if (closed) {
      return;
    }
    sessionGone = false;
    revokedByServer = false;
    connectAttempts = 0;
    if (!socket) {
      connect();
      return;
    }
    socket.disconnect();
    socket.connect();
  };

  return {
    connect,
    reconnect,
    close() {
      closed = true;
      cancelTimers();
      socket?.removeAllListeners();
      socket?.disconnect();
    },
    sendMutation: (mutation) =>
      new Promise<MutationAck>((resolve, reject) => {
        const current = socket;
        if (!current || closed || !current.connected) {
          reject(new ApiError(0, "NETWORK_FAILED"));
          return;
        }
        const schema = createMutationAckSchema(
          classId,
          canvasId,
          mutation.epoch,
        );
        current
          .timeout(ackTimeout)
          .emit("canvas:mutation", mutation, (error, ack) => {
            if (error) {
              reject(new ApiError(0, "NETWORK_FAILED"));
              return;
            }
            const parsed = schema.safeParse(ack);
            if (parsed.success) {
              resolve(parsed.data);
            } else {
              reject(new ApiError(0, "INVALID_RESPONSE"));
            }
          });
      }),
    sendPointer(update) {
      if (socket?.connected && !closed) {
        socket.volatile.emit("pointer:update", update);
      }
    },
    sendLock(request) {
      if (socket?.connected && !closed) {
        socket.emit("lesson:lock", request);
      }
    },
    sendCompletion(toggle) {
      if (!socket?.connected || closed) {
        return false;
      }
      socket.emit("lesson:completion", toggle);
      return true;
    },
    sendCompletionMode(request) {
      if (socket?.connected && !closed) {
        socket.emit("lesson:completionMode", request);
      }
    },
    sendCompletionReset(request) {
      if (socket?.connected && !closed) {
        socket.emit("lesson:reset", request);
      }
    },
    sendStudentComments(request) {
      if (socket?.connected && !closed) {
        socket.emit("lesson:studentComments", request);
      }
    },
    sendPresence(update) {
      if (socket?.connected && !closed) {
        socket.volatile.emit("presence:update", update);
      }
    },
    verify: (canvasId) =>
      new Promise<CanvasVerifyAck>((resolve, reject) => {
        const current = socket;
        if (!current || closed || !current.connected) {
          reject(new ApiError(0, "NETWORK_FAILED"));
          return;
        }
        current
          .timeout(ackTimeout)
          .emit("canvas:verify", { canvasId }, (error, ack) => {
            if (error) {
              // Covers both a real timeout and an old server that never
              // acknowledges an event it doesn't recognize — both need to
              // fail quietly, so a caller doesn't need to tell them apart.
              reject(new ApiError(0, "NETWORK_FAILED"));
              return;
            }
            const parsed = CanvasVerifyAckSchema.safeParse(ack);
            if (parsed.success) {
              resolve(parsed.data);
            } else {
              reject(new ApiError(0, "INVALID_RESPONSE"));
            }
          });
      }),
    repair: (request) =>
      new Promise<CanvasRepairAck>((resolve, reject) => {
        const current = socket;
        if (!current || closed || !current.connected) {
          reject(new ApiError(0, "NETWORK_FAILED"));
          return;
        }
        current
          .timeout(ackTimeout)
          .emit("canvas:repair", request, (error, ack) => {
            if (error) {
              reject(new ApiError(0, "NETWORK_FAILED"));
              return;
            }
            const parsed = CanvasRepairAckSchema.safeParse(ack);
            if (parsed.success) {
              resolve(parsed.data);
            } else {
              reject(new ApiError(0, "INVALID_RESPONSE"));
            }
          });
      }),
    diagnostic(payload) {
      if (socket?.connected && !closed) {
        socket.emit("canvas:diagnostic", payload);
      }
    },
  };
};
